import BaseController from "niteroi/packages/system/controllers/Base.controller";
import {GenericTile$PressEvent} from "sap/m/GenericTile";
import JSONModel from "sap/ui/model/json/JSONModel";
import Column from "sap/ui/table/Column";
import Label from "sap/m/Label";
import Text from "sap/m/Text";
import Dialog from "sap/m/Dialog";
import Logger from "sap/base/Log";
import FileUploader, {FileUploader$ChangeEvent, FileUploader$TypeMissmatchEvent} from "sap/ui/unified/FileUploader";
import Ui5File from "sap/ui/core/util/File";
import Fragment from "sap/ui/core/Fragment";
import SearchField from "sap/m/SearchField";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ListBinding from "sap/ui/model/ListBinding";
import Context from "sap/ui/model/Context";

/**
 * Main controller for the Air Passenger Rights package.
 * Manages header tile navigation and active state via a ViewModel.
 *
 * @namespace niteroi.packages.air-passenger-rights.controllers
 */
export default class Main extends BaseController {
    protected uploadDialog: Promise<Dialog> | null = null;
    protected uploadModel: JSONModel;

    /**
     * Initialize controller, load airlines model, and set up view model for tile state
     */
    public onInit(): void {
        super.onInit();
        // Initialize view model for tile active state
        const viewModel = new JSONModel({
            activeTile: "claims"
        });
        this.getView().setModel(viewModel, "tileState");
    }

    public onBeforeRendering(): void {
        this.setActiveTileByRoute();
    }

    /**
     * Set the active tile based on the current route using the ViewModel
     */
    protected setActiveTileByRoute(): void {
        const router = this.getRouter();
        const currentRoute = router?.getHashChanger()?.getHash();

        const routeToTileMap: Record<string, string> = {
            "airlines": "airlines",
            "airports": "airports",
            "flights": "flights",
            "claims": "claims",
            "passengers": "passengers",
            "": "claims"
        };

        const tileStateModel = this.getModel("tileState");
        let activeTile = (tileStateModel.getProperty("/activeTile") as string) ?? "claims";

        for (const [route, tileKey] of Object.entries(routeToTileMap)) {
            if (currentRoute?.includes(route)) {
                activeTile = tileKey;
                break;
            }
        }

        if (tileStateModel) {
            tileStateModel.setProperty("/activeTile", activeTile);
        }
    }

    /**
     * Handle press event on header tiles.
     * Updates the ViewModel active tile and navigates to the target route.
     *
     * @param event - Press event from GenericTile
     */
    public handleHeaderTilePress(event: GenericTile$PressEvent): void {
        event.preventDefault();

        const tile = event.getSource();
        const routeName = tile.getUrl();

        if (routeName) {
            const tileKey = routeName.split("/").pop() || "claims";
            const tileStateModel = this.getModel("tileState");
            if (tileStateModel) {
                tileStateModel.setProperty("/activeTile", tileKey);
            }
            this.navTo(routeName, {}, false);
        }
    }

    protected sendBatch(items: object[]): Promise<void> {
        const tileStateModel = this.getModel("tileState");
        const activeTile = tileStateModel.getProperty("/activeTile") as string;

        return new Promise((resolve, reject) => {
            const headers = this.getRequestHeaders();
            const requestModel = this.createRequestModel();
            requestModel
                .loadData(`/api/air-passenger-rights/${activeTile}`,
                    JSON.stringify(items),
                    true,
                    "POST",
                    false,
                    true,
                    headers
                )
                .then(() => resolve())
                .catch((err: unknown) => reject(err));
        });
    }

    public onUploadCancel(): void {
        this.uploadDialog?.then((dialog) => {
            dialog.close();
            const uploader = this.byId("idFileUploader") as FileUploader;
            if (uploader) uploader.setValue("");
            if (this.uploadModel) this.uploadModel.setData({items: []});
        });
    }

    public async onPressUploadBtn(): Promise<void> {
        const data = this.uploadModel.getProperty("/items") as [];
        if (!data || data.length === 0) return;

        const dialog = await this.uploadDialog;
        if (dialog) dialog.setBusy(true);

        const batchSize = 100; // Mindful of backend processing
        const total = data.length;
        let processed = 0;
        let errors = 0;

        // Sequential processing to avoid flooding server
        for (let i = 0; i < total; i += batchSize) {
            const chunk = data.slice(i, i + batchSize);
            try {
                await this.sendBatch(chunk);
                processed += chunk.length;
                this.showToast(this.getText("msgUploadProgress", [processed, total]));
            } catch (error) {
                Logger.error("Batch upload failed", JSON.stringify(error), this.getMetadata().getName());
                errors += chunk.length;
                this.showToast(this.getText("msgUploadCompletedWithErrors", [errors]));
            }
        }

        if (dialog) dialog.setBusy(false);
        this.onUploadCancel();

        if (errors > 0) {
            this.showToast(this.getText("msgUploadCompletedWithErrors", [errors]));
        } else {
            this.showToast(this.getText("msgUploadCompletedSuccess"));
        }

        // Refresh main list
        this.getData();
    }

    protected getData(): void {
    }

    protected fetchData(url: string, modelName: string, objectId: string): void {
        const model = this.createRequestModel();
        model.loadData(url, "", true, "GET", false, true, this.getRequestHeaders()).then(() => {
            this.originalData = objectId ? Object.assign({}, JSON.parse(model.getJSON())) : {};
            this.setModel(model, modelName);
            if (!objectId) {
                const searchField = this.byId("idSearchFieldTable") as SearchField;
                if (searchField) {
                    const searchValue = searchField.getValue();
                    if (searchValue) {
                        searchField.fireSearch({query: searchValue});
                    }
                }
            }
            this.getView().setBusy(false);
        });
    }

    protected loadCollectionData(url: string, modelName: string): void {
        this.getView().setBusy(true);
        const model = this.createRequestModel();
        model.setData([]);
        this.setModel(model, modelName);
        model.loadData(url, "", true, "GET", false, true, this.getRequestHeaders())
            .finally(() => {
                this.getView().setBusy(false);
            });
    }

    public onFileChange(event: FileUploader$ChangeEvent): void {
        const file = (event.getParameter("files") as File[])?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            let data: object[] = [];
            try {
                if (file.type === "application/json" || file.name.toLowerCase().endsWith(".json")) {
                    data = JSON.parse(content);
                }

                if (!Array.isArray(data)) {
                    data = [data]; // Handle single object
                }

                if (data.length > 0) {
                    this.validateAndBindData(data);
                } else {
                    this.showToast(this.getText("msgFileEmpty"));
                }
            } catch (err) {
                Logger.error("Error parsing file", err as string, "Airlines.controller.ts");
                this.showToast(this.getText("msgErrorParsingFile"));
            }
        };
        reader.readAsText(file);
    }

    public onTypeMismatch(event: FileUploader$TypeMissmatchEvent): void {
        const fileType = event.getParameter("fileType");
        const supportedFileTypes = (event.getSource()).getFileType();
        this.showToast(this.getText("msgFileTypeNotSupported", [fileType, supportedFileTypes.join(", ")]));
    }

    protected validateAndBindData(data: object[]): void {
        const sample = data[0];
        // Minimal validation
        if (!sample) return;

        this.uploadModel.setProperty("/items", data);

        // Dynamic Table Columns
        this.uploadModel.setProperty("/columns", Object.keys(sample).map(columnName => ({
            label: this.getText(columnName),
            key: columnName
        })));
    }

    public onPressMenuItemImport(): void {
        if (!this.uploadDialog) {
            this.uploadDialog = Fragment.load({
                id: this.getView().getId(),
                name: "niteroi.packages.air-passenger-rights.fragments.UploadDialog",
                controller: this
            }) as Promise<Dialog>;
        }

        this.uploadDialog.then((dialog) => {
            this.getView().addDependent(dialog);
            this.uploadModel = new JSONModel({items: [], columns: []});
            dialog.setModel(this.uploadModel, "uploadData");
            dialog.open();
        });
    }

    protected exportToJSON(name: string): void {
        const data = this.getModel(name).getData() as [];
        if (!data || !data.length) {
            this.showToast(this.getText("msgNoDataToExport"));
            return;
        }
        Ui5File.save(JSON.stringify(data, null, 4), name, "json", "application/json", "utf-8");
    }

    public onPressRemoveAllPreviewItems(): void {
        this.uploadModel.setData({items: [], columns: []});
    }

    public columnFactory(id: string, context: Context): Column {
        const columnData = context.getObject() as { label: string, key: string};

        return new Column(id, {
            autoResizable: true,
            hAlign: "Begin",
            resizable: true,
            showFilterMenuEntry: true,
            width: "auto",
            label: new Label({
                text: columnData.label
            }),
            template: new Text({
                text: `{uploadData>${columnData.key}}`,
                wrapping: false
            })
        });
    }

    protected bindFilters(binding: ListBinding, searchQuery: string): void {
        if (!binding) {
            return;
        }

        let filters: Array<Filter> = [];

        // If search query is empty, reset to default filters
        if (!searchQuery || searchQuery.trim() === "") {
            binding.filter(filters);
            return;
        }

        filters = this.createAirportFilters(searchQuery);
        binding.filter(filters);
    }

    protected createAirportFilters(searchQuery: string): Filter[] {
        // Create filters for each searchable field with case-insensitive Contains
        const searchFilters: Filter[] = [
            new Filter("iataCode", FilterOperator.Contains, searchQuery),
        ];

        if (searchQuery.length > 2) {
            searchFilters.push(new Filter("icaoCode", FilterOperator.Contains, searchQuery));
        }

        if (searchQuery.length > 3) {
            const extendedSearchFilters: Filter[] = [
                new Filter("name", FilterOperator.Contains, searchQuery),
                new Filter("country", FilterOperator.Contains, searchQuery),
                new Filter("continent", FilterOperator.Contains, searchQuery),
                new Filter("stateCode", FilterOperator.Contains, searchQuery),
                new Filter("city", FilterOperator.Contains, searchQuery),
                new Filter("type", FilterOperator.Contains, searchQuery),
                new Filter("keywords", FilterOperator.Contains, searchQuery)
            ];
                searchFilters.push(...extendedSearchFilters);
        }

        // Combine all search filters with OR logic
        const combinedSearchFilter = new Filter({
            filters: searchFilters,
            and: false // OR condition - match any field
        });

        // Keep the base filters for valid codes (AND with search)
        const baseFilters = [
            new Filter("iataCode", FilterOperator.NE, null),
            new Filter("icaoCode", FilterOperator.NE, null)
        ];

        // Combine base filters with search filter using AND logic
        return [
            ...baseFilters,
            combinedSearchFilter
        ];
    }
}
